import { useTranslation } from "react-i18next";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { AppButton, AppInput, Divider, FigmaTypography } from "@libs/zapi-ui/custom";
import { TrashSimpleIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useRef, useState, useEffect } from "react";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { toast } from "sonner";
import { useUpdateUserProfile } from "../services/useUpdateUserProfile";
import { useDeleteUserPhoto } from "../services/useDeleteUserPhoto";

export function UserSettings() {
  const { t } = useTranslation("settings");
  const { firstName, lastName, profileImageUrl, refetch } = useMeStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: updateUserProfile, isPending: isSaving } = useUpdateUserProfile();
  const { mutate: deleteUserPhoto, isPending: isDeleting } = useDeleteUserPhoto();

  const [formData, setFormData] = useState({ firstName: "", lastName: "" });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({ firstName: firstName ?? "", lastName: lastName ?? "" });
  }, [firstName, lastName]);

  const isChanged = formData.firstName !== firstName || formData.lastName !== lastName || selectedFile !== null;

  function fileClickHandler() {
    fileInputRef.current?.click();
  }

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error(t("profile.invalidFileType"));
      return t("profile.invalidFileType");
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("profile.fileTooLarge"));
      return t("profile.fileTooLarge");
    }

    return null;
  }

  async function fileChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleRemovePhoto = async () => {
    setUploadError(null);

    deleteUserPhoto(undefined, {
      onSuccess: async () => {
        toast.success(t("profile.photoRemoved"));
        if (refetch) await refetch();
      },
      onError: (error) => {
        console.error("Failed to remove profile photo:", error);
        const err = error as { response?: { data?: { message?: string } } };
        setUploadError(err.response?.data?.message || t("profile.photoRemoveFailed"));
        toast.error(t("profile.photoRemoveFailed"));
      }
    });
  };

  const handleSaveChanges = async () => {
    setUploadError(null);

    updateUserProfile(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        file: selectedFile ?? undefined
      },
      {
        onSuccess: async () => {
          toast.success(t("profile.updated"));
          if (refetch) await refetch();

          setSelectedFile(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        onError: (error) => {
          toast.error(t("profile.updateFailed"));
          console.error("Failed to update profile:", error);
        }
      }
    );
  };

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <FigmaTypography variant="heading-3" color="zinc900">
        {t("profile.heading")}
      </FigmaTypography>

      <div className="flex flex-col gap-8">
        <div className="flex gap-6">
          <button
            className={cn(
              "border-none outline-none cursor-pointer flex items-center justify-center",
              "w-[88px] h-[88px] rounded-full bg-innoveraOrange-400 overflow-hidden"
            )}
            onClick={fileClickHandler}
          >
            {(previewUrl || profileImageUrl) && (
              <img src={previewUrl || profileImageUrl || ""} alt="user img" className="w-full h-full object-cover" />
            )}
            {!previewUrl && !profileImageUrl && (
              <FigmaTypography className="text-[40px] text-white">{firstName?.[0]}</FigmaTypography>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={fileChangeHandler}
          />

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <AppButton
                appearance="contained"
                color="zinc100"
                startAdornment={<UploadSimpleIcon />}
                onClick={fileClickHandler}
                disabled={isSaving || isDeleting}
              >
                {t("profile.uploadPhoto")}
              </AppButton>
              {profileImageUrl && (
                <AppButton
                  appearance="text"
                  startAdornment={<TrashSimpleIcon />}
                  onClick={handleRemovePhoto}
                  disabled={isSaving}
                  isLoading={isDeleting}
                  loadingText={t("profile.removing")}
                >
                  {t("profile.removePhoto")}
                </AppButton>
              )}
            </div>
            <div>
              <FigmaTypography variant="body-3" color="zinc500">
                {t("profile.photoHintSize")}
              </FigmaTypography>
              <FigmaTypography variant="body-3" color="zinc500">
                {t("profile.photoHintFormat")}
              </FigmaTypography>
              {uploadError && (
                <FigmaTypography variant="body-3" className="text-red-500 mt-1">
                  {uploadError}
                </FigmaTypography>
              )}
            </div>
          </div>
        </div>

        <Divider className="!bg-innoveraZinc-100" />

        <div className="flex gap-1">
          <AppInput
            label={t("profile.firstName")}
            className="w-[250px]"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            disabled={isSaving || isDeleting}
          />
          <AppInput
            label={t("profile.lastName")}
            className="w-[250px]"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            disabled={isSaving || isDeleting}
          />
        </div>

        <AppButton
          className="w-fit"
          onClick={handleSaveChanges}
          disabled={isSaving || isDeleting || !isChanged}
          isLoading={isSaving}
          loadingText={t("common.saving")}
        >
          {t("common.save")}
        </AppButton>
      </div>
    </div>
  );
}
